import { snackbar } from "@bcmi-labs/art-ui"
import { createFileRoute, redirect } from "@tanstack/react-router"

import { $iotApiClient } from "@/api/iot"
import { invalidateCachedQueries } from "@/api/query/invalidateCachedQueries"
import { getXOrganizationHeaders } from "@/api/utils"
import { routeGuard } from "@/lib/routing/beforeLoad"
import { getRecap } from "@/lib/routing/loader"
import { queryClient } from "@/lib/services/instances"
import { ga4Emitter } from "@/lib/utils/analytics"

export const Route = createFileRoute("/_authlayout/_header/things/new/")({
  beforeLoad: async ({ context }) => {
    await routeGuard(context, { permission: { flag: "iot:thing::create", fallbackRoute: { to: "/things" } } })
  },
  loader: async ({ context, preload }) => {
    // This route only exists to create a new Thing and redirect to its page: do nothing if preloading.
    if (preload) return

    const spaceId = context.spaceStorage.getSpace()
    const user = await context.authClient.getUser()

    // This should not even be possible 🤷🏻
    if (!user) throw new Error("User not authenticated")

    const recap = await getRecap(context)

    if (recap.isAtQuota.things) {
      snackbar({
        message: "You have reached the maximum number of Things for your plan.",
        variant: "info"
      })
      redirect({ to: "/things", throw: true })
      return
    }

    const newThing = await $iotApiClient.PUT("/iot/v2/things", {
      params: { header: getXOrganizationHeaders(spaceId) },
      body: {
        timezone: user.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
      }
    })

    if (newThing.error) {
      snackbar({
        message: `Failed to create Thing: ${newThing.error.detail}`,
        variant: "error"
      })
      throw redirect({ to: "/things" })
    }

    ga4Emitter({ type: "THING_CREATE", payload: { thing_id: newThing.data.id } })

    // Invalidate the things list query
    invalidateCachedQueries(spaceId, queryClient).invalidateByGroupKind("THING")

    redirect({ to: "/things/$id", params: { id: newThing.data.id }, search: { tab: "variables" }, throw: true })
  },
  head: () => ({
    meta: [
      {
        title: "New Thing | Arduino Cloud"
      }
    ]
  })
})
